// Bottom-sheet booking flow for partner services.
// Handles 3 payment paths: Razorpay gateway, Membership credits, Combo enquiry (no upfront).
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import useProfileGate from '../../hooks/useProfileGate';
import { useAuth } from '../../context/AuthContext';
import { getMembership, isMembershipActive } from '../../utils/membership';
import { loadRazorpayScript, verifyRazorpayPayment, withRazorpayCheckoutConfig } from '../../utils/razorpay';

const SLOTS = ['Morning', 'Afternoon', 'Evening'];

const fmtINR = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n);

export default function BookingSheet({ open, service, variant, user, onClose, onBooked }) {
  const [city, setCity] = useState('');
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('gateway');
  const [membership, setMembership] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const gate = useProfileGate();
  const { user: authUser } = useAuth();

  const cities = service?.available_cities?.length ? service.available_cities : ['Mumbai'];
  const isCombo = service?.combo_only || variant?.price == null;
  const price = variant?.price || 0;

  useEffect(() => {
    if (!open) return;
    setCity(cities[0]);
    setDate('');
    setSlot('');
    setPhone(user?.mobile_number || '');
    setNotes('');
    setPaymentMethod(isCombo ? 'enquiry' : 'gateway');
    getMembership().then(setMembership);
  }, [open, service, variant]);  // eslint-disable-line

  if (!open || !service || !variant) return null;

  const membershipActive = isMembershipActive(membership);
  const credits = membershipActive ? membership.balance_remaining ?? 0 : 0;
  const canUseCredits = !isCombo && membershipActive && credits >= price;

  const todayISO = new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    if (!city || !date || !slot) {
      toast.error('Please fill city, date and slot');
      return;
    }
    // The booking needs a mobile number on the profile (asked for once, in place)
    if (!(await gate.ensure('service_booking'))) return;
    const contactPhone = phone || authUser?.mobile_number || '';
    if (!contactPhone) {
      toast.error('Please enter a contact phone');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post(
        `/services/${service.id}/bookings`,
        {
          variant_id: variant.id,
          city,
          preferred_date: date,
          preferred_slot: slot,
          contact_phone: contactPhone,
          contact_email: user?.email || null,
          notes,
          payment_method: paymentMethod,
        },
      );

      // Combo / credits → already finalised on backend
      if (!data.requires_payment) {
        toast.success(
          isCombo
            ? 'Enquiry sent! The spa will contact you with options.'
            : 'Booking confirmed via membership credits!',
        );
        onBooked?.(data.booking);
        return;
      }

      // Razorpay flow
      await loadRazorpayScript();
      const rzp = new window.Razorpay(withRazorpayCheckoutConfig({
        key: data.razorpay_key_id,
        amount: Math.round(data.amount * 100),
        currency: 'INR',
        name: 'Backyard',
        description: `${service.name} · ${variant.label}`,
        order_id: data.razorpay_order_id,
        prefill: {
          email: user?.email || '',
          contact: phone,
          name: user?.name || '',
        },
        theme: { color: '#2B3A67' },
        handler: async (resp) => {
          try {
            await axiosInstance.post(
              `/services/bookings/${data.booking.id}/confirm-payment`,
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
            );
            toast.success('Booking confirmed!');
            onBooked?.(data.booking);
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled. Your booking is held as pending.');
          },
        },
      }));
      rzp.open();
    } catch (e) {
      if (await gate.handleError(e)) toast.info('Thanks — tap Book again to finish.');
      else toast.error(e.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="booking-sheet"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-[#2B3A67]" data-testid="booking-sheet-title">
              {isCombo ? 'Enquire' : 'Book'} · {service.name}
            </h2>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-0.5">
              {variant.label} {!isCombo && `· ${fmtINR(price)}`}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="booking-sheet-close"
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"
          >
            <i className="ph-bold ph-x" />
          </button>
        </div>

        {/* City */}
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
          City
        </label>
        <select
          data-testid="booking-city"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="w-full mb-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-medium"
        >
          {cities.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        {/* Date */}
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
          Preferred Date
        </label>
        <input
          data-testid="booking-date"
          type="date"
          min={todayISO}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full mb-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />

        {/* Slot */}
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
          Preferred Slot
        </label>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {SLOTS.map((s) => (
            <button
              key={s}
              data-testid={`booking-slot-${s.toLowerCase()}`}
              onClick={() => setSlot(s)}
              className={`px-3 py-2 rounded-xl text-xs font-bold border-2 ${
                slot === s
                  ? 'border-[#2B3A67] bg-[#2B3A67] text-white'
                  : 'border-gray-200 text-gray-500'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Phone */}
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
          Contact Phone
        </label>
        <input
          data-testid="booking-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91"
          className="w-full mb-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />

        {/* Notes */}
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
          Notes (optional)
        </label>
        <textarea
          data-testid="booking-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything we should tell the spa?"
          className="w-full mb-4 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />

        {/* Payment Method */}
        {!isCombo && (
          <>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
              Payment
            </label>
            <div className="space-y-2 mb-5">
              <label
                data-testid="booking-pay-gateway"
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${
                  paymentMethod === 'gateway' ? 'border-[#2B3A67] bg-[#2B3A67]/5' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  checked={paymentMethod === 'gateway'}
                  onChange={() => setPaymentMethod('gateway')}
                />
                <div className="flex-1">
                  <p className="text-sm font-bold">Pay {fmtINR(price)} via Razorpay</p>
                  <p className="text-xs text-gray-400">Cards · UPI · Netbanking</p>
                </div>
              </label>
              <label
                data-testid="booking-pay-credits"
                className={`flex items-center gap-3 p-3 rounded-xl border-2 ${
                  canUseCredits ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                } ${
                  paymentMethod === 'credits' ? 'border-[#2B3A67] bg-[#2B3A67]/5' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  disabled={!canUseCredits}
                  checked={paymentMethod === 'credits'}
                  onChange={() => setPaymentMethod('credits')}
                />
                <div className="flex-1">
                  <p className="text-sm font-bold">Use Membership Credits</p>
                  <p className="text-xs text-gray-400">
                    {membershipActive
                      ? `Available: ${fmtINR(credits)}${credits < price ? ' · insufficient' : ''}`
                      : 'No active membership'}
                  </p>
                </div>
              </label>
            </div>
          </>
        )}

        {isCombo && (
          <div className="bg-[#FEF3C7] text-[#92400E] text-xs font-bold rounded-xl p-3 mb-5">
            This treatment is best combined with a body scrub or massage. The spa will reach out
            with combo options and final pricing.
          </div>
        )}

        <button
          data-testid="booking-submit"
          disabled={submitting}
          onClick={handleSubmit}
          className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-50"
        >
          {submitting
            ? 'Processing...'
            : isCombo
            ? 'Send Enquiry'
            : paymentMethod === 'credits'
            ? `Confirm with Credits`
            : `Pay ${fmtINR(price)} & Book`}
        </button>
      </div>
      {gate.sheet}
    </div>
  );
}
